import toast from 'react-hot-toast'
import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { IoChevronBack, IoCheckmarkCircle, IoCloseCircle } from 'react-icons/io5'

import { Input, PasswordEyeInput } from '@/components/forms'
import { Button, Card, Spinner } from '@/components/ui'
import { useAuthStore } from '@/stores/useAuthStore'
import { ROUTES } from '@/constants/routes'
import { sanitizeEmail } from '@/lib/sanitize'
import { logger } from '@/lib/logger'
import { SEO } from '@/components/features/SEO'
import type { SignupRequest } from '@/types/auth'

import authStyles from '../Auth.module.css'
import styles from './Register.module.css'

export function Register() {
    const navigate = useNavigate()
    const { register, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<SignupRequest>({
        email: '',
        password1: '',
        password2: '',
    })

    useEffect(() => {
        if (isLogged) {
            navigate(ROUTES.home)
        }
    }, [isLogged, navigate])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState: SignupRequest) => ({
            ...prevState,
            [key]: value,
        }))
    }

    // Password strength requirements
    const passwordRequirements = useMemo(() => {
        const password = formData.password1
        return {
            minLength: password.length >= 8,
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        }
    }, [formData.password1])

    const validateForm = (formData: SignupRequest) => {
        const { email, password1, password2 } = formData

        if (!email || !password1 || !password2) {
            return { isValid: false, message: 'Por favor completa todos los campos' }
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return { isValid: false, message: 'Por favor ingresa un correo válido' }
        }

        // Password strength validation - check all requirements
        if (!passwordRequirements.minLength) {
            return { isValid: false, message: 'La contraseña debe tener al menos 8 caracteres' }
        }
        if (!passwordRequirements.hasUppercase) {
            return { isValid: false, message: 'La contraseña debe tener al menos una mayúscula' }
        }
        if (!passwordRequirements.hasLowercase) {
            return { isValid: false, message: 'La contraseña debe tener al menos una minúscula' }
        }
        if (!passwordRequirements.hasNumber) {
            return { isValid: false, message: 'La contraseña debe tener al menos un número' }
        }
        if (!passwordRequirements.hasSpecialChar) {
            return { isValid: false, message: 'La contraseña debe tener al menos un carácter especial' }
        }

        // Password confirmation validation
        if (password1 !== password2) {
            return { isValid: false, message: 'Las contraseñas no coinciden' }
        }

        return { isValid: true, message: '' }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const validation = validateForm(formData);
        if (!validation.isValid) {
            toast.error(validation.message);
            return;
        }

        // Sanitize inputs before sending to API
        const sanitizedData: SignupRequest = {
            email: sanitizeEmail(formData.email),
            password1: formData.password1, // Don't sanitize passwords
            password2: formData.password2, // Don't sanitize passwords
        }

        try {
            const status: boolean = await register(sanitizedData);
            if (status) {
                navigate(ROUTES.verifyEmail);
                return;
            }
            toast.error('Error al registrarse');
        } catch (error) {
            toast.error('Ocurrió un error durante el registro');
            logger.error('Registration error:', error);
        }
    }

    return (
        <div className={authStyles.container}>
            <SEO
                title="Crear Cuenta - Peru IA"
                description="Registrate en Peru IA para guardar tus conversaciones y acceder a funcionalidades completas."
                url="https://peruhonesto.com/register"
                noindex
            />
            <div className={authStyles.header}>
                <button
                    className={authStyles.backButton}
                    onClick={() => navigate(ROUTES.home)}
                    aria-label="Volver al inicio"
                >
                    <IoChevronBack /> Volver al Inicio
                </button>
            </div>

            <Card className={authStyles.card}>
                <h1 className={authStyles.title}>Crear Cuenta</h1>

                <form className={authStyles.form} onSubmit={handleSubmit}>
                    <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        setValue={(v) => handleFormDataChange('email', v)}
                        label="Correo electrónico"
                        placeholder="Ingresa tu correo"
                    />
                    <div>
                        <PasswordEyeInput
                            name="password1"
                            value={formData.password1}
                            setValue={(v) => handleFormDataChange('password1', v)}
                            label="Contraseña"
                            placeholder="Ingresa tu contraseña"
                        />

                        {formData.password1 && (
                            <div className={styles.passwordRequirements}>
                                <div className={`${styles.requirement} ${passwordRequirements.minLength ? styles.met : ''}`}>
                                    {passwordRequirements.minLength ? (
                                        <IoCheckmarkCircle className={styles.iconMet} />
                                    ) : (
                                        <IoCloseCircle className={styles.iconUnmet} />
                                    )}
                                    <span>Al menos 8 caracteres</span>
                                </div>
                                <div className={`${styles.requirement} ${passwordRequirements.hasUppercase ? styles.met : ''}`}>
                                    {passwordRequirements.hasUppercase ? (
                                        <IoCheckmarkCircle className={styles.iconMet} />
                                    ) : (
                                        <IoCloseCircle className={styles.iconUnmet} />
                                    )}
                                    <span>Una letra mayúscula</span>
                                </div>
                                <div className={`${styles.requirement} ${passwordRequirements.hasLowercase ? styles.met : ''}`}>
                                    {passwordRequirements.hasLowercase ? (
                                        <IoCheckmarkCircle className={styles.iconMet} />
                                    ) : (
                                        <IoCloseCircle className={styles.iconUnmet} />
                                    )}
                                    <span>Una letra minúscula</span>
                                </div>
                                <div className={`${styles.requirement} ${passwordRequirements.hasNumber ? styles.met : ''}`}>
                                    {passwordRequirements.hasNumber ? (
                                        <IoCheckmarkCircle className={styles.iconMet} />
                                    ) : (
                                        <IoCloseCircle className={styles.iconUnmet} />
                                    )}
                                    <span>Un número</span>
                                </div>
                                <div className={`${styles.requirement} ${passwordRequirements.hasSpecialChar ? styles.met : ''}`}>
                                    {passwordRequirements.hasSpecialChar ? (
                                        <IoCheckmarkCircle className={styles.iconMet} />
                                    ) : (
                                        <IoCloseCircle className={styles.iconUnmet} />
                                    )}
                                    <span>Un carácter especial (!@#$%...)</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <PasswordEyeInput
                        name="password2"
                        value={formData.password2}
                        setValue={(v) => handleFormDataChange('password2', v)}
                        label="Confirmar Contraseña"
                        placeholder="Confirma tu contraseña"
                    />

                    <p className={styles.legalNotice}>
                        Al registrarte, aceptas nuestros{' '}
                        <span
                            className={styles.legalLink}
                            onClick={() => navigate(ROUTES.terms)}
                        >
                            Terminos de Servicio
                        </span>{' '}
                        y confirmas que has leido nuestra{' '}
                        <span
                            className={styles.legalLink}
                            onClick={() => navigate(ROUTES.privacy)}
                        >
                            Politica de Privacidad
                        </span>.
                    </p>

                    <Button
                        variant="primary"
                        size="md"
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner variant="secondary" size="sm" /> : 'Registrarse'}
                    </Button>
                </form>

                <div className={authStyles.footer}>
                    <span>¿Ya tienes una cuenta?</span>
                    <button className="text-btn" onClick={() => navigate(ROUTES.login)}>
                        <span className={authStyles.link}>Iniciar Sesión</span>
                    </button>
                </div>
            </Card>
        </div>
    )
}
